import {
  AccessibleContent,
  Button,
  Flex,
  Heading,
  IconAddLine,
  InlineList,
  ScreenReaderContent,
  Tag,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import { memo, useState } from "react";
import { Card } from "../../../Card";
import emailVerification from "../utils";

const ReviewersCard = memo(
  ({ plan, handleUpdateApprover, handleAddReviewer, handleRemoveReviewer }) => {
    const [approver, setApprover] = useState(
      plan?.results_purl?.approvers ?? ""
    );
    const [invalidApprover, setInvalidApprover] = useState(false);
    const [reviewer, setReviewer] = useState("");
    const [invalidReviewer, setInvalidReviewer] = useState(false);
    const [duplicateReviewer, setDuplicateReviewer] = useState(false);
    const [reviewers, setReviewers] = useState(
      plan?.reviewers?.split(",") ?? []
    );

    const verifyApproverEmail = (email) => {
      if (emailVerification(email)) {
        setInvalidApprover(false);
      } else {
        setInvalidApprover(true);
      }
    };

    const addReviewer = () => {
      if (!emailVerification(reviewer)) {
        setInvalidReviewer(true);
        setDuplicateReviewer(false);
      } else if (reviewers.includes(reviewer)) {
        setDuplicateReviewer(true);
        setInvalidReviewer(false);
      } else {
        setInvalidReviewer(false);
        setDuplicateReviewer(false);
        setReviewers((prevList) => [...prevList, reviewer]);
        setReviewer("");
        handleAddReviewer(reviewer);
      }
    };

    const removeReviewer = (index) => {
      setReviewers(reviewers.filter((_, i) => index !== i));
      handleRemoveReviewer(index);
    };

    return (
      <>
        <Heading level="h4" as="h2" margin="0 0 small 0" color="primary">
          {t("assessment.planResultsReviewers")}:
        </Heading>
        <Card style={{ backgroundColor: "rgb(242, 248, 250)", height: "auto" }}>
          <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
            {t("assessment.approver")}
          </Heading>
          <Flex>
            <Flex.Item>
              <TextInput
                renderLabel={
                  <ScreenReaderContent>
                    {t("assessment.approver")}
                  </ScreenReaderContent>
                }
                display="inline-block"
                placeholder={t("assessment.email")}
                size="small"
                value={approver}
                onChange={(e) => setApprover(e.target.value)}
                onBlur={() => {
                  if (approver !== "") {
                    verifyApproverEmail(approver);
                  }
                  handleUpdateApprover(approver);
                }}
                messages={
                  invalidApprover
                    ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                    : []
                }
              />
            </Flex.Item>
          </Flex>
          <Heading as="h3" level="h5" border="bottom" margin="small 0">
            {t("assessment.reviewers")}
          </Heading>
          <Flex gap="small" alignItems="start" wrap="wrap">
            <Flex.Item>
              <TextInput
                id="plan-reviewers-input"
                renderLabel={
                  <ScreenReaderContent>
                    {t("assessment.email")}
                  </ScreenReaderContent>
                }
                placeholder={t("assessment.email")}
                size="small"
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
                messages={
                  invalidReviewer
                    ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                    : duplicateReviewer
                      ? [
                          {
                            text: t("assessment.reviewerAlreadyAdded"),
                            type: "error",
                          },
                        ]
                      : []
                }
              />
            </Flex.Item>
            <Flex.Item>
              <Button
                size="small"
                renderIcon={IconAddLine}
                onClick={addReviewer}
              >
                {t("global.add")}
              </Button>
            </Flex.Item>
          </Flex>
          <InlineList>
            {reviewers.map((email, index) => (
              <InlineList.Item key={index} margin="small 0 0 0">
                <Tag
                  text={
                    <AccessibleContent alt={t("assessment.deleteReviewer")}>
                      {email}
                    </AccessibleContent>
                  }
                  dismissible
                  onClick={() => removeReviewer(index)}
                />
              </InlineList.Item>
            ))}
          </InlineList>
        </Card>
      </>
    );
  }
);

export default ReviewersCard;
